import { css } from '@/styled-system/css';

// Exported for regression-testing the :visited override (HP-8397).
const defaultLinkColor = 'text.onImpact.link.fixed.default' as const;
export const footerAnchorOverrides = {
  color: defaultLinkColor,
  _visited: { color: defaultLinkColor },
  _hover: { color: 'text.onImpact.link.fixed.hover' },
  _active: { color: 'text.onImpact.link.fixed.pressed' },
};

export const footer = css({
  // 'blue.600', not 'blue600' — the bare name is not a token, compiles to
  // invalid CSS, and silently drops the background (white footer in light
  // mode: invisible white text + failing link contrast).
  background: 'blue.600',
  color: 'white',
  paddingTop: 'loose',
  paddingBottom: 'loose',
  paddingLeft: 'default',
  paddingRight: 'default',
  textAlign: 'center',
  // This surface is the same dark navy in BOTH themes (raw blue.600 has no
  // _dark variant), but the onImpact link tokens assume impact surfaces
  // lighten in dark mode and darken their teal (_dark teal.500 is ~2.75:1
  // here). Pin every footer link to the fixed onImpact chain so contrast
  // holds in both modes (teal.400 is 5.35:1 on blue.600). _visited is
  // included so visited links don't revert to browser-default purple.
  '& a': footerAnchorOverrides,
});

export const crossbrandNav = css({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  listStyle: 'none',

  padding: 0,

  margin: 0,
  marginBottom: 'default',
  gap: 'tighter',
});

export const footerNav = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'tighter',
  marginBottom: 'default',
});

export const linkList = css({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  listStyle: 'none',

  padding: 0,

  margin: 0,
  gap: 'tighter',
  _last: {
    marginBottom: 'default',
  },
});

export const linkItem = css({
  fontSize: 'body-sm',
  lineHeight: 'heading-sm',
});

export const linkButton = css({
  appearance: 'none',
  background: 'transparent',
  border: 'none',
  // Fixed onImpact chain, matching the footer's `& a` rule — see the
  // rationale on `footer` above.
  color: 'text.onImpact.link.fixed.default',
  cursor: 'pointer',
  focusOutline: true,
  font: 'inherit',
  padding: 0,
  textDecoration: 'underline',
  _hover: {
    color: 'text.onImpact.link.fixed.hover',
    textDecoration: 'none',
  },
  _active: {
    color: 'text.onImpact.link.fixed.pressed',
  },
  _focusVisible: {
    color: 'text.onImpact.link.fixed.hover',
  },
});

export const crossbrandItem = css({
  fontSize: 'body-sm',
  lineHeight: 'heading-sm',
});

export const badgeSection = css({
  display: 'flex',
  justifyContent: 'center',
  gap: 'default',
  marginTop: 'tighter',
  marginBottom: 'default',
});

export const badgeImg = css({
  display: 'block',
});

export const textSection = css({
  fontSize: 'body-xs',
  lineHeight: 'body-xs',
  color: 'white',
  marginBottom: 'tighter',
  // eslint-disable-next-line hotpads/no-magic-numbers -- design-specific dimension; follow-up tokenization
  maxWidth: '600px',
  marginLeft: 'auto',
  marginRight: 'auto',
});

export const fairHousingLink = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'tightest',
});

export const hudIcon = css({
  display: 'inline-block',
  verticalAlign: 'middle',
});

export const copyrightSection = css({
  fontSize: 'body-xs',
  color: 'white',
  marginTop: 'tighter',
});
